import { useState, useCallback } from 'react';

import Switch from '@ui/switch/switch';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import { PatchToggleTestStream } from '@/connection/sessions/toggle-test-stream';
import { useAppDispatch } from '@/store/hooks';
import { setWorkingSessionTestStream } from '@/store/slices/project';
import { useWorkingSession } from '@/utils/use-working-session';
import Text from '@ui/text/text/text';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type TestStreamToggleProps = {
  showTooltip?: boolean;
  primaryThemeColor?: boolean;
};

const TestStreamToggle = ({ showTooltip, primaryThemeColor }: TestStreamToggleProps) => {
  const dispatch = useAppDispatch();
  const session = useWorkingSession();
  const sessionUuid = session?.uuid;
  const isTestStream = !!session?.streaming_options?.test_stream;

  const [isTogglingTestStream, setIsTogglingTestStream] = useState(false);

  const handleToggleTestStream = useCallback(
    async (_value: string, turnedOn: boolean) => {
      if (!sessionUuid) return;
      try {
        setIsTogglingTestStream(true);
        const response = await PatchToggleTestStream(sessionUuid, turnedOn);
        if (response?.message) {
          toastTrigger(response.message, { type: EToastTypes.warning });
          return;
        }
        dispatch(setWorkingSessionTestStream({ sessionUuid, testStream: turnedOn }));
      } catch (error) {
        console.error(error);
        toastTrigger('Error updating Test Mode', { type: EToastTypes.error });
      } finally {
        setIsTogglingTestStream(false);
      }
    },
    [dispatch, sessionUuid]
  );

  return (
    <TooltipPortal
      tooltipContent={
        <Text className="P2" color="var(--primitives-mode-1-gray-100)">
          Test Mode ON
        </Text>
      }
      disabled={!showTooltip}
      position={TooltipPortalPosition.TOP}
      delayStart={150}
      childrenWrapperClass="flex ai-center"
      animationType="slide-up"
    >
      <Switch
        value="test-broadcast"
        on={isTestStream}
        onClick={handleToggleTestStream}
        disabled={isTogglingTestStream}
        loading={isTogglingTestStream}
        color={primaryThemeColor ? getThemeColorCSSVariable() : 'var(--global-warning-400)'}
      />
    </TooltipPortal>
  );
};

export default TestStreamToggle;
